import { CommonModule } from '@angular/common';
import { ApplicationRef, Component, OnInit, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { EventType, NavigationEnd, Router, RouterLink } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';

import { TranslocoModule } from '@jsverse/transloco';
import { navbarLinks, breadCrumbLinkNames } from './navbar-links';
import { LanguageService } from '../../services/languageservice/language.service';
import PermissionsUtil from '../../util/permissions-util';
import { initFlowbite } from 'flowbite';
import { UserService } from '../../../platform/services/user-service/userservice.service';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { FormsModule } from '@angular/forms';
import {
  DateTimeSettingsState,
  DATE_FORMAT_OPTIONS,
  type DateFormatOption,
} from '../../state-stores/date-time-settings.state';
import {
  matArrowRight,
  matKeyboardArrowDown,
  matHome,
  matPerson,
  matList,
  matFormatListBulleted,
  matWidgets,
  matSwapHoriz,
  matLocationOn,
  matGroup,
  matVpnKey,
  matReceipt,
  matAttachMoney,
  matPayment,
  matCategory,
  matApps,
  matConfirmationNumber,
  matPlace,
  matKeyboardArrowLeft,
  matKeyboardArrowRight,
  matCreditCard,
  matAssessment,
  matWaterDrop,
  matAccountBalanceWallet,
  matReceiptLong,
  matSettings,
  matPlaylistAddCheck,
  matEmail,
} from '@ng-icons/material-icons/baseline';
import { matDashboard } from '@ng-icons/material-icons/baseline';
import { matDownload } from '@ng-icons/material-icons/baseline';
import { matHistory } from '@ng-icons/material-icons/baseline';
import { AppDataState } from '../../state-stores/app-data-state.state';
import { AppInstanceService } from '../../../platform/services/app-instance-service/app-instance-service.service';
import { App } from '../../../platform/interfaces/app';
import { AppInstance } from '../../../platform/interfaces/app-instance';
import { ConsumerService } from '../../../flowpoint-apps/station-app/services/consumer-service/consumer.service';

/**
 * NavbarComponent is responsible for rendering the navigation bar UI.
 * It handles dynamic link visibility based on permissions,
 * localization, user actions (like logout), and responsive menus.
 */
@Component({
  selector: 'app-navbar',
  imports: [CommonModule, TranslocoModule, RouterLink, NgIconComponent, FormsModule],
  providers: [
    provideIcons({
      matEmail,
      matAssessment,
      matCreditCard,
      matConfirmationNumber,
      matPlace,
      matArrowRight,
      matKeyboardArrowDown,
      matHome,
      matPerson,
      matList,
      matFormatListBulleted,
      matWidgets,
      matSwapHoriz,
      matLocationOn,
      matGroup,
      matVpnKey,
      matReceipt,
      matAttachMoney,
      matPayment,
      matCategory,
      matApps,
      matKeyboardArrowLeft,
      matKeyboardArrowRight,
      matWaterDrop,
      matAccountBalanceWallet,
      matReceiptLong,
      matSettings,
      matPlaylistAddCheck,
      matDashboard,
      matDownload,
      matHistory,
    }),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './navbar.component.html',
})
export class NavbarComponent implements OnInit {
  /**
   * Stores readable names for breadcrumb routes.
   */
  breadCrumbLinkNames = breadCrumbLinkNames;

  /**
   * Navigation links specific to the active application.
   */
  navLinks: any[] = [];

  /**
   * Permissions currently granted to the logged-in user.
   */
  permissionSet: string[] = [];

  /**
   * Type of the active permission set (e.g. Consumer, Operator).
   */
  permissionSetType = '';

  /**
   * Tracks whether the hamburger menu is open.
   */
  isHamburgerMenuOpen = false;

  /**
   * Tracks whether the navbar is collapsed (showing only icons).
   */
  isCollapsed = false;

  /** Whether the Date/Time Settings modal is visible. */
  showDateTimeSettingsModal = false;

  /** Staged date format selection (applied when user saves). */
  stagedDateFormat: string = '';

  /** Available date format options for the settings dropdown. */
  readonly dateFormatOptions: DateFormatOption[] = DATE_FORMAT_OPTIONS;

  /**
   * Event emitter to notify parent component when navbar is toggled.
   */

  appInstances: AppInstance[] = [];
  hideMakePaymentForCreditConsumer = false;
  // Consumer type doesn't change mid-session; cache by operator to avoid refetching on every navigation.
  private hideMakePaymentCacheKey: string | null = null;
  @Output() navbarToggled = new EventEmitter<boolean>();

  constructor(
    private readonly userSerivce: UserService,
    protected readonly router: Router,
    protected readonly authService: MsalService,
    private readonly languageService: LanguageService,
    private readonly operatorState: AppDataState,
    private readonly appInstanceService: AppInstanceService,
    private readonly consumerService: ConsumerService,
    private readonly dateTimeSettings: DateTimeSettingsState,
    private readonly appRef: ApplicationRef,
  ) {}

  /**
   * Initializes the component.
   * - Sets up Flowbite components.
   * - Retrieves and caches permission sets.
   * - Sets nav links based on the current app and permissions.
   *
   * Each data fetch is independently guarded and retried once after a short delay: a failure in
   * one (e.g. a transient MFA_REQUIRED error from a request that raced a just-completed MFA
   * verification) must not prevent the other from populating the nav links, and a single retry
   * lets the navbar self-heal from that kind of one-off race instead of staying empty for the
   * rest of the session.
   */
  async ngOnInit(): Promise<void> {
    initFlowbite();

    this.appInstances = await this.loadAppInstancesWithRetry();

    const appName = this.router.url.split('/')[1];
    this.getAppPermissionSet(appName);
    this.subscribeToRouterEvents();
  }

  /**
   * Subscribes to router events to dynamically update the nav links
   * and permission set when navigation occurs.
   */
  subscribeToRouterEvents() {
    this.router.events.subscribe((val) => {
      if (val instanceof NavigationEnd) {
        const appName = val.url.split('/')[1];
        this.getAppPermissionSet(appName);
        
        // Close all submenus on navigation
        this.navLinks.forEach((link) => {
          if (link.children) {
            // Check if the current route is a child of this parent
            const isChildActive = link.children.some(
              (child: any) => this.getPathWithoutParams(val.url) === child.path
            );
            // Keep parent open only if one of its children is the active route
            link.isOpen = isChildActive;
          }
        });
      }
    });
  }

  /**
   * Loads app instances, retrying once after a short delay on failure (e.g. a transient
   * MFA_REQUIRED error from a request that raced a just-completed MFA verification) so the
   * navbar self-heals instead of staying permanently empty for the rest of the session.
   */
  private async loadAppInstancesWithRetry(): Promise<AppInstance[]> {
    try {
      return await this.appInstanceService.getAppInstances();
    } catch (err) {
      console.error('[Navbar] Failed to load app instances, retrying once:', err);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      try {
        return await this.appInstanceService.getAppInstances();
      } catch (retryErr) {
        console.error('[Navbar] Retry loading app instances also failed:', retryErr);
        return [];
      }
    }
  }

  /**
   * Fetches permission set for the given app and builds nav links accordingly, retrying once
   * after a short delay on failure (see loadAppInstancesWithRetry for why).
   * @param appName Name of the app (e.g. 'platform', 'stationapp')
   */
  getAppPermissionSet(appName: string) {
    this.applyAppPermissionSet(appName).catch(async (err) => {
      console.error('[Navbar] Failed to load permission set / nav links, retrying once:', err);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      this.applyAppPermissionSet(appName).catch((retryErr) => {
        console.error('[Navbar] Retry loading permission set / nav links also failed:', retryErr);
      });
    });
  }

  private applyAppPermissionSet(appName: string): Promise<void> {
    return PermissionsUtil.getPermissionSets(this.userSerivce).then(async (permissions) => {
      const operatorId =
        appName === 'platform' ? '0' : this.operatorState.getOperatorId();
      const permissionSetId = this.operatorState.getPermissionSetId();
      const permissionSetKey =
        appName === 'platform'
          ? 'platform-0'
          : `${appName}-${operatorId}-${permissionSetId}`;
      const permissionSetData = permissions.get(permissionSetKey);

      if (appName === 'platform') {
        this.permissionSet = permissionSetData?.permissions ?? [];
      } else {
        this.permissionSet = permissionSetData?.permissions ?? [];
      }
      this.permissionSetType = permissionSetData?.permissionSetType ?? '';
      const appNavLinks = [
        ...(navbarLinks[appName as keyof typeof navbarLinks] ?? []),
      ];

      if (appName === 'stationapp' && this.permissionSetType.toLowerCase() === 'user') {
        const cacheKey = `${operatorId}-${permissionSetId}`;
        if (this.hideMakePaymentCacheKey !== cacheKey) {
          this.hideMakePaymentCacheKey = cacheKey;
          await this.setMakePaymentVisibilityByConsumerType();
        }
      } else {
        this.hideMakePaymentCacheKey = null;
        this.hideMakePaymentForCreditConsumer = false;
      }

      this.navLinks = this.hideMakePaymentForCreditConsumer
        ? appNavLinks.filter((link) => link.path !== '/stationapp/payment')
        : appNavLinks;

      localStorage.setItem('permissionSet', JSON.stringify(this.permissionSet));
      localStorage.setItem('navLinks', JSON.stringify(this.navLinks));
      localStorage.setItem('permissionSetType', this.permissionSetType);
    });
  }

  private async setMakePaymentVisibilityByConsumerType(): Promise<void> {
    try {
      const consumers = (await this.consumerService.get()).data;
      const consumer = consumers?.[0];
      this.hideMakePaymentForCreditConsumer =
        !!consumer?.isCredit && !consumer?.isPrepay;
    } catch {
      this.hideMakePaymentForCreditConsumer = false;
    }
  }

  private shouldHideNavLink(navLink: any): boolean {
    if (navLink.path === '/stationapp/payment') {
      const hasPaymentModule =
        this.checkModule('Moneris Payment') || this.checkModule('Worldline Payment');
      return this.hideMakePaymentForCreditConsumer || !hasPaymentModule;
    }
    return false;
  }

  isPermissionSetTypeVisible(navLink: any): boolean {
    const currentPermissionType = this.permissionSetType.toLowerCase();
    const visibleForTypes = (navLink.showForPermissionSetTypes ?? []).map((type: string) =>
      type.toLowerCase(),
    );
    const hiddenForTypes = (navLink.hideForPermissionSetTypes ?? []).map((type: string) =>
      type.toLowerCase(),
    );

    if (visibleForTypes.length > 0 && !visibleForTypes.includes(currentPermissionType)) {
      return false;
    }

    if (hiddenForTypes.includes(currentPermissionType)) {
      return false;
    }

    return true;
  }

  isNavLinkVisible(navLink: any): boolean {
    if (
      this.shouldHideNavLink(navLink) ||
      !this.isPermissionSetTypeVisible(navLink) ||
      !this.isFinancialDataVisible(navLink) ||
      !this.checkModule(navLink.module)
    ) {
      return false;
    }

    // A parent link's own permissions are ignored; it shows if any child is visible.
    if (navLink.children?.length) {
      return navLink.children.some((child: any) => this.isNavLinkVisible(child));
    }

    return this.checkPermission(navLink.permissions ?? [], navLink.superUserAccess ?? true);
  }

  get hideFinancialDataForOperator(): boolean {
    return this.operatorState.getHideFinancialDataForOperator();
  }

  get hideFinancialDataForCustomer(): boolean {
    return this.operatorState.getHideFinancialDataForCustomer();
  }

  /**
   * Determines whether a nav link should be visible given the current HideFinancialData configuration.
   * - `hideWhenFinancialDataHidden` hides the link for everyone when financial data is hidden.
   * - `hideForCustomerWhenFinancialDataHidden` hides the link only for customer (permission set type 'user') logins.
   */
  isFinancialDataVisible(navLink: any): boolean {
    const isCustomer = this.permissionSetType.toLowerCase() === 'user';
    const hideFinancialData = isCustomer ? this.hideFinancialDataForCustomer : this.hideFinancialDataForOperator;
    if (!hideFinancialData) {
      return true;
    }
    if (navLink.hideWhenFinancialDataHidden) {
      return false;
    }
    if (navLink.hideForCustomerWhenFinancialDataHidden && isCustomer) {
      return false;
    }
    return true;
  }

  /**
   * Toggles the dropdown visibility for a specific nav link.
   * @param navLink Navigation link object
   */
  toggleDropdown(navLink: any) {
    navLink.isOpen = !navLink.isOpen;
  }

  /**
   * Toggles the visibility of the hamburger menu for smaller screens.
   */
  toggleHamburgerMenu() {
    this.isHamburgerMenuOpen = !this.isHamburgerMenuOpen;
    const navbarUser = document.getElementById('navbar-user');
    if (navbarUser != null) {
      if (this.isHamburgerMenuOpen) {
        navbarUser.classList.remove('hidden');
        this.navLinks.forEach((navLink) => (navLink.isOpen = false));
      } else {
        navbarUser.classList.add('hidden');
        this.navLinks.forEach((navLink) => (navLink.isOpen = false));
      }
    }
  }

  /**
   * Toggles the navbar between collapsed (icons only) and expanded (icons + labels) states.
   */
  toggleNavbar() {
    this.isCollapsed = !this.isCollapsed;
    this.navbarToggled.emit(this.isCollapsed);
  }

  /**
   * Closes the hamburger menu and hides the user nav section.
   */
  collapseHamburgerMenu() {
    this.isHamburgerMenuOpen = false;
    document.getElementById('navbar-user')?.classList.add('hidden');
  }

  /**
   * Keeps a nav link dropdown open.
   * @param navLink The nav link to keep open
   */
  keepDropdownOpen(navLink: any) {
    navLink.isOpen = true;
  }

  /**
   * Navigates to the profile page.
   */
  navigateToProfile() {
    this.router.navigate(['/profile']);
  }

  /**
   * Navigates to a specified route path.
   * @param path The route to navigate to
   */
  navigateTo(path: string) {
    this.router.navigate([path]);
  }

  /**
   * Clears browser storage, logs out the current user, and redirects to the logout URL.
   */
  logout() {
    sessionStorage.clear();
    localStorage.clear();
    this.authService.logoutRedirect();
  }

  /**
   * Changes the current language based on user selection.
   * @param event HTML select change event
   */
  switchLanguage(event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    const lang = selectElement.value;
    this.languageService.switchLanguage(lang);
  }

  /**
   * Gets the current logged-in user's name.
   * @returns User's display name or empty string
   */
  getUserName(): string {
    const activeAccount = this.authService.instance.getActiveAccount();
    return activeAccount ? activeAccount.name! : '';
  }

  /**
   * Navigates to the first child of a parent nav link.
   * @param navLink Parent navigation link with children
   */
  navigateToFirstChild(navLink: any): void {
    if (navLink.children && navLink.children.length > 0) {
      const firstAccessibleChild = navLink.children.find((child: any) =>
        this.checkPermission(child.permissions ?? [], child.superUserAccess ?? true)
      );
      if (firstAccessibleChild) {
        navLink.isOpen = true;
        this.router.navigate([firstAccessibleChild.path]);
      }
    }
  }

  toggleNavLink(navLink: any): void {
    if (navLink.children) {
      // Check if any child matches the current route
      const isChildSelected = navLink.children.some(
        (child: any) => this.router.url === child.path,
      );

      // Keep the parent open if a child is selected
      if (isChildSelected) {
        navLink.isOpen = true;
      } else {
        // Toggle the parent if no child is selected
        navLink.isOpen = !navLink.isOpen;
      }
    } else {
      // If it's a child or non-expandable link, navigate to its path
      this.router.navigate([navLink.path]);

      // Ensure the parent of the selected child remains open
      this.navLinks.forEach((link) => {
        if (link.children && link.children.includes(navLink)) {
          link.isOpen = true;
        } else {
          link.isOpen = false;
        }
      });
    }
  }

  /**
   * Checks whether the current user has all required permissions.
   * @param permissions List of required permission strings
   * @returns True if the user has all permissions, otherwise false
   */
  checkPermission(permissions: string[], superUserAccess = true): boolean {
    return PermissionsUtil.checkPermissions(
      permissions,
      this.permissionSet,
      superUserAccess,
    );
  }

  checkModule(module: string): boolean {
    if (!module) {
      return true;
    }
    
    var appInstance = this.appInstances.find(
      (instance) => instance.id === this.operatorState.getAppInstanceId(),
    );

    return appInstance?.modules?.some((mod) => mod.name === module) ?? false;
  }

  /**
   * Constructs a complete path for a given segment from the current URL.
   * Ensures `/home` is appended if only the app segment is present.
   * @param segment Route segment to preserve
   * @param url Full current route
   * @returns A complete path string
   */
  getFullPath(segment: string, url: string): string {
    const path = url.includes(segment) ? url.split(segment)[0] + segment : url;
    return path.split('/').filter(Boolean).length === 1 ? `${path}/home` : path;
  }

  /**
   * Removes any query parameters from a URL path.
   * @param path The URL path string
   * @returns Cleaned path without query strings
   */
  getPathWithoutParams(path: string): string {
    return path.split('?')[0];
  }

  // ── Date/Time Settings Modal ────────────────────────────────────────────────

  /** Opens the Date/Time Settings modal, pre-populating with current values. */
  openDateTimeSettings(): void {
    this.stagedDateFormat = this.dateTimeSettings.dateFormat();
    this.showDateTimeSettingsModal = true;
  }

  /** Closes the modal without saving. */
  closeDateTimeSettings(): void {
    this.showDateTimeSettingsModal = false;
  }

  /** Saves the staged settings and closes the modal. */
  saveDateTimeSettings(): void {
    this.dateTimeSettings.setDateFormat(this.stagedDateFormat);
    this.showDateTimeSettingsModal = false;
    // Force a full synchronous CD cycle across the whole application so that
    // every component (including those in separate layout branches) re-renders
    // the date columns with the new format / timezone immediately.
    this.appRef.tick();
  }

  /** Returns the current date format label for the collapsed-mode tooltip. */
  getDateTimeSettingsSummary(): string {
    const fmt = this.dateFormatOptions.find(o => o.value === this.dateTimeSettings.dateFormat());
    const fmtLabel = fmt ? fmt.value : this.dateTimeSettings.dateFormat();
    return `Format: ${fmtLabel}`;
  }

  /** Preview the staged format/timezone using today's date. */
  getPreviewDate(): Date {
    return new Date();
  }
}
